import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, downloadJobResult } from '../api.js'
import { Card, Field, FileField, Alert, StatusPill, JobProgress, useJobElapsed, usePolling, UserSearch } from '../lib.jsx'
import PageViewerEditor from './editor/PageViewerEditor.jsx'
import { TEMPLATE_TYPES, TypeIcons } from '../templateTypes.jsx'

const TABS = [
  { key: 'editor', label: 'Edytor' },
  { key: 'export', label: 'Eksport' },
  { key: 'versions', label: 'Wersje i historia' },
  { key: 'share', label: 'Udostępnianie' },
]

const KIND_PL = { heading: 'nagłówek', paragraph: 'akapit', quote: 'cytat', bullet_list: 'lista punktowana',
  numbered_list: 'lista numerowana', table: 'tabela', image: 'obraz', chart: 'wykres' }

function useJob(jobId, opts) {
  return usePolling(api.job, jobId, opts)
}

// ---- Krok „Treść”: import pliku/tekstu + lista jednostek Document Modelu ----
// `importJobId`/`setImportJobId` są PODNIESIONE do rodzica (ProjectWorkspace), nie żyją tu lokalnie:
// to pozwala rodzicowi (a) wznowić polowanie po odświeżeniu strony (mount effect woła
// `api.activeJob`, ustawia ten sam id — `usePolling` podejmuje wątek jakby nic się nie stało), (b)
// wiedzieć że trwa aktywny job (gating zakładek), bez duplikowania stanu w dwóch miejscach.
function SemanticUnitRow({ projectId, unit, nextUnit, reloadUnits }) {
  const [value, setValue] = useState((unit.content || {}).text || (unit.content?.items || []).join('\n'))
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      const content = unit.kind.includes('list') ? { ...unit.content, items: value.split('\n').map(x => x.trim()).filter(Boolean) } : { ...unit.content, text: value, html: '' }
      await api.patchUnit(projectId, unit.id, content); await reloadUnits()
    } finally { setBusy(false) }
  }
  const split = async () => {
    const blocks = unit.content?.blocks || []
    if (blocks.length < 2) return
    const raw = window.prompt(`Podziel po którym akapicie? 1–${blocks.length - 1}`, '1')
    const at = Number(raw)
    if (!Number.isInteger(at)) return
    setBusy(true); try { await api.splitUnit(projectId, unit.id, at); await reloadUnits() } finally { setBusy(false) }
  }
  const merge = async () => {
    if (!nextUnit) return
    setBusy(true); try { await api.mergeUnits(projectId, unit.id, nextUnit.id); await reloadUnits() } finally { setBusy(false) }
  }
  return <div className="semantic-unit">
    <div className="semantic-unit-head"><span className="kind-tag">{KIND_PL[unit.kind] || unit.kind} · {unit.content?.semantic_role || 'treść'}</span><div>{(unit.content?.blocks || []).length > 1 && <button className="btn btn-sm btn-ghost" disabled={busy} onClick={split}>Rozdziel</button>}{nextUnit?.kind === unit.kind && <button className="btn btn-sm btn-ghost" disabled={busy} onClick={merge}>Połącz z następnym</button>}</div></div>
    {(unit.kind === 'paragraph' || unit.kind === 'heading' || unit.kind.includes('list')) ? <div className="semantic-edit"><textarea rows={Math.min(6, Math.max(2, value.split('\n').length))} value={value} onChange={e => setValue(e.target.value)} /><button className="btn btn-sm" disabled={busy || value === ((unit.content || {}).text || (unit.content?.items || []).join('\n'))} onClick={save}>Zapisz</button></div> : <div className="small muted">{unit.kind === 'image' ? unit.content?.image_ref : 'Element strukturalny edytowany na podglądzie.'}</div>}
  </div>
}

function ContentTab({ projectId, units, reloadUnits, importJobId, setImportJobId, setComposeJobId }) {
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const importJob = useJob(importJobId, { onDone: async () => {
    setImportJobId(null); await reloadUnits()
    try { const res = await api.compose(projectId); setComposeJobId(res.job_id) } catch (e) { setErr(e.message) }
  } })

  const confirmReplace = () => {
    if (units.length === 0) return true
    return window.confirm(`Import nowego pliku USUNIE i zastąpi całą obecną treść projektu (${units.length} jednostek). Kontynuować?`)
  }

  const submitFile = async (e) => {
    e.preventDefault()
    if (!file) return
    if (!confirmReplace()) return
    setErr(''); setBusy(true)
    try {
      const fd = new FormData(); fd.append('file', file)
      const res = await api.importFile(projectId, fd)
      setImportJobId(res.job_id); setFile(null)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const submitText = async (e) => {
    e.preventDefault()
    if (!text.trim()) return
    if (!confirmReplace()) return
    setErr(''); setBusy(true)
    try {
      const res = await api.importText(projectId, { text, format: 'text' })
      setImportJobId(res.job_id); setText('')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const running = importJob && importJob.status === 'running'
  const importElapsed = useJobElapsed(importJob)

  const groups = units.reduce((acc, unit) => {
    const c = unit.content || {}; const key = `${c.chapter_id ?? 0}:${c.partial || 'pozostale'}`
    if (!acc[key]) acc[key] = { chapter: c.chapter_id ?? 0, partial: c.partial || 'pozostale', units: [] }
    acc[key].units.push(unit); return acc
  }, {})

  return (
    <div className="content-editor-panel">
      {(units.length === 0 || importJobId) && <Card title="Import treści" desc="Zaimportuj plik (DOCX/PDF/TXT/MD/HTML) lub wklej tekst. Po imporcie skład uruchomi się automatycznie.">
        <Alert kind="err">{err}</Alert>
        {(importJobId || running) && (
          <Alert kind="info">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span><span className="spin-sm" style={{ marginRight: 8 }} />Import w toku (analiza AI treści)… {importJob?.status && <StatusPill status={importJob.status} />}</span>
              <span className="mono small">{importElapsed}</span>
            </div>
          </Alert>
        )}
        <JobProgress job={importJob} showTime={false} />
        <form onSubmit={submitFile} style={{ marginBottom: 22 }}>
          <FileField label="Plik" accept=".docx,.pdf,.txt,.md,.html" file={file} onPick={setFile} />
          <button className="btn btn-primary" disabled={busy || !file}>{busy ? 'Wysyłanie…' : 'Importuj plik'}</button>
        </form>
        <button type="button" className="text-link" onClick={() => setText(v => v ? '' : ' ')}>{text ? 'Ukryj pole tekstowe' : 'lub wklej tekst'}</button>
        {text && <form onSubmit={submitText}><Field label="Wklej tekst"><textarea value={text.trimStart()} onChange={e => setText(e.target.value)} rows={6} placeholder="Wklej treść dokumentu…" /></Field><button className="btn" disabled={busy || !text.trim()}>Importuj tekst</button></form>}
      </Card>}
      {units.length > 0 && <details className="document-model-panel">
        <summary><b>Document Model</b> · {units.length} jednostek pogrupowanych w partiale</summary>
        <div className="document-model-groups">{Object.entries(groups).map(([key, group]) => <section key={key} className="document-partial-group"><h3>Rozdział {group.chapter + 1} · {group.partial}</h3>{group.units.map((unit, index) => <SemanticUnitRow key={unit.id} projectId={projectId} unit={unit} nextUnit={group.units[index + 1]} reloadUnits={reloadUnits} />)}</section>)}</div>
      </details>}
    </div>
  )
}

// ---- Krok „Eksport” ----
function ExportTab({ projectId, hasPages }) {
  const [jobs, setJobs] = useState({}) // format -> job_id
  const [err, setErr] = useState('')

  const FORMATS = [
    { key: 'pdf', label: 'PDF', ext: 'pdf', desc: 'Pełny dokument / prezentacja PDF (wg typu szablonu).' },
    { key: 'pptx_pixel', label: 'PPTX (pixel-perfect)', ext: 'pptx', desc: 'Zrzut każdej strony jako obraz — wygląd 1:1 z podglądem.' },
    { key: 'pptx_editable', label: 'PPTX (edytowalny)', ext: 'pptx', desc: 'Natywne obiekty PowerPoint tam, gdzie to możliwe (fallback do obrazu per slajd).' },
  ]

  const start = async (fmt) => {
    setErr('')
    try { const res = await api.exportProject(projectId, fmt); setJobs(j => ({ ...j, [fmt]: res.job_id })) }
    catch (e) { setErr(e.message) }
  }

  return (
    <div className="grid grid-3">
      <Alert kind="err">{err}</Alert>
      {!hasPages && <Alert kind="warn">Projekt nie ma jeszcze złożonych stron — najpierw uruchom kompozycję w zakładce „Edytor”.</Alert>}
      {FORMATS.map(f => (
        <ExportCard key={f.key} format={f} jobId={jobs[f.key]} disabled={!hasPages} onStart={() => start(f.key)} />
      ))}
    </div>
  )
}
function ExportCard({ format, jobId, disabled, onStart }) {
  const job = useJob(jobId, { onDone: (j) => downloadJobResult(j.id, `eksport.${format.ext}`) })
  const running = job && (job.status === 'queued' || job.status === 'running')
  return (
    <Card title={format.label} desc={format.desc}>
      {job && <div style={{ marginBottom: 10 }}><StatusPill status={job.status} /></div>}
      <button className="btn btn-primary" disabled={disabled || running} onClick={onStart}>
        {running ? 'Generowanie…' : (job?.status === 'done' ? 'Wygeneruj ponownie' : 'Eksportuj')}
      </button>
      {job?.status === 'error' && <Alert kind="err">{job.error_msg}</Alert>}
    </Card>
  )
}

// ---- Krok „Wersje i historia” ----
function VersionsTab({ projectId }) {
  const [versions, setVersions] = useState([])
  const [history, setHistory] = useState([])
  const [label, setLabel] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => { api.versions(projectId).then(setVersions).catch(e => setErr(e.message)); api.history(projectId).then(setHistory).catch(() => {}) }
  useEffect(load, [projectId])

  const save = async () => {
    setBusy(true); setErr('')
    try { await api.saveVersion(projectId, label); setLabel(''); load() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const restore = async (v) => {
    setBusy(true); setErr('')
    try { await api.restoreVersion(projectId, v); load() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="grid grid-2" style={{ alignItems: 'start' }}>
      <Card title="Wersje projektu">
        <Alert kind="err">{err}</Alert>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <input placeholder="Etykieta wersji (opcjonalnie)" value={label} onChange={e => setLabel(e.target.value)} />
          <button className="btn btn-primary" disabled={busy} onClick={save}>Zapisz wersję</button>
        </div>
        {versions.length === 0 ? <div className="empty">Brak zapisanych wersji.</div> : (
          <table>
            <thead><tr><th>Wersja</th><th>Etykieta</th><th>Data</th><th /></tr></thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.id}>
                  <td className="mono">v{v.version}</td>
                  <td>{v.label || '—'}</td>
                  <td className="muted small">{new Date(v.created_at).toLocaleString('pl-PL')}</td>
                  <td><button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => restore(v.version)}>Przywróć</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Historia zmian" desc="Najnowsze pierwsze">
        {history.length === 0 ? <div className="empty">Brak historii.</div> : (
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            {history.map(h => (
              <div key={h.id} className="kvp">
                <span>{h.description}</span>
                <span className="muted small">{new Date(h.created_at).toLocaleString('pl-PL')}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

// ---- Krok „Udostępnianie” ----
function ShareTab({ projectId }) {
  const [shares, setShares] = useState([])
  const [picked, setPicked] = useState(null)
  const [role, setRole] = useState('viewer')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => api.shares(projectId).then(setShares).catch(e => setErr(e.message))
  useEffect(() => { load() }, [projectId])

  const add = async () => {
    if (!picked) return
    setBusy(true); setErr('')
    try { await api.addShare(projectId, picked.id, role); setPicked(null); load() }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const remove = async (id) => {
    try { await api.removeShare(projectId, id); load() } catch (e) { setErr(e.message) }
  }

  return (
    <Card title="Udostępnianie projektu" desc="Znajdź użytkownika po nazwie lub e-mailu i przydziel rolę Editor lub Viewer.">
      <Alert kind="err">{err}</Alert>
      <div className="row" style={{ marginBottom: 18, alignItems: 'flex-start' }}>
        <div style={{ flex: 2, minWidth: 220 }}>
          {picked ? (
            <div className="unit-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><b>{picked.username}</b> <span className="muted small">{picked.email}</span></span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPicked(null)}>Zmień</button>
            </div>
          ) : (
            <UserSearch onPick={setPicked} exclude={shares.map(s => s.user_id)} />
          )}
        </div>
        <select value={role} onChange={e => setRole(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
        </select>
        <button className="btn btn-primary" disabled={busy || !picked} onClick={add}>Udostępnij</button>
      </div>
      {shares.length === 0 ? <div className="empty">Projekt nie jest jeszcze udostępniony nikomu.</div> : (
        <table>
          <thead><tr><th>Użytkownik</th><th>Rola</th><th /></tr></thead>
          <tbody>
            {shares.map(s => (
              <tr key={s.id}>
                <td><b>{s.username}</b><div className="muted small">{s.email}</div></td>
                <td><span className="role-tag">{s.role}</span></td>
                <td><button className="btn btn-sm btn-danger" onClick={() => remove(s.id)}>Usuń</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

export default function ProjectWorkspace() {
  const { id } = useParams()
  const nav = useNavigate()
  const [project, setProject] = useState(null)
  const [units, setUnits] = useState([])
  const [pages, setPages] = useState([])
  const [tab, setTab] = useState('editor')
  const [err, setErr] = useState('')
  const [dupBusy, setDupBusy] = useState(false)
  const [delBusy, setDelBusy] = useState(false)
  const [typeBusy, setTypeBusy] = useState(false)
  // Id jobów import/kompozycja PODNIESIONE tutaj (nie lokalnie w ContentTab/PageViewerEditor) —
  // to jedyny sposób, żeby po odświeżeniu strony (mount effect niżej, `api.activeJob`) wznowić
  // dokładnie ten sam job w dziecku ORAZ jednocześnie wiedzieć w rodzicu, że trwa aktywne zadanie
  // (gating zakładek poniżej), bez trzymania tego samego stanu w dwóch miejscach.
  const [importJobId, setImportJobId] = useState(null)
  const [composeJobId, setComposeJobId] = useState(null)

  const reloadUnits = () => api.units(id).then(setUnits).catch(e => setErr(e.message))
  const reloadPagesCount = () => api.pages(id).then(setPages).catch(() => {})

  useEffect(() => {
    api.project(id).then(setProject).catch(e => setErr(e.message))
    reloadUnits(); reloadPagesCount()
    // Wznowienie po odświeżeniu strony (Faza 10): jeśli w chwili odświeżenia trwał import/
    // kompozycja, podejmij dokładnie ten sam job — 404 (brak aktywnego joba) to normalny stan,
    // `api.activeJob` już zwraca wtedy null, nic więcej nie robimy.
    api.activeJob(id).then(job => {
      if (!job) return
      if (job.type === 'pdfgen_import') setImportJobId(job.id)
      else if (job.type === 'pdfgen_compose') setComposeJobId(job.id)
    }).catch(() => {})
  }, [id])

  const duplicate = async () => {
    setDupBusy(true)
    try { const d = await api.duplicateProject(id); nav(`/projects/${d.id}`) } catch (e) { setErr(e.message) } finally { setDupBusy(false) }
  }

  const remove = async () => {
    if (!window.confirm(`Usunąć projekt „${project.name}” bezpowrotnie? Tej operacji nie można cofnąć.`)) return
    setDelBusy(true)
    try { await api.deleteProject(id); nav('/') } catch (e) { setErr(e.message); setDelBusy(false) }
  }

  const changeType = async (outputType) => {
    if (!outputType || outputType === project.output_types?.[0]) return
    setTypeBusy(true); setErr('')
    try {
      const updated = await api.patchProject(id, { output_types: [outputType] })
      setProject(updated); setPages([])
      if (units.length > 0) {
        const job = await api.compose(id)
        setComposeJobId(job.job_id)
      }
    } catch (e) { setErr(e.message) } finally { setTypeBusy(false) }
  }

  if (err && !project) return <div className="content"><Alert kind="err">{err}</Alert></div>
  if (!project) return <div className="content"><div className="empty">Wczytywanie…</div></div>

  // Gating zakładek (Faza 10): bez treści nie ma co edytować/eksportować; podczas aktywnego
  // importu/kompozycji reszta zakładek czeka, żeby nie operować na danych w trakcie zmiany.
  // `activeJobBusy` opiera się na tym samym podniesionym stanie co wznowienie polowania —
  // oba dziecko-komponenty czyszczą go (`setImportJobId(null)`/`setComposeJobId(null)`) w `onDone`.
  const hasContent = units.length > 0
  const activeJobBusy = importJobId != null || composeJobId != null

  return (
    <>
      <div className="topbar">
        <div>
          <button className="btn btn-sm btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav('/')}>‹ Projekty</button>
          <h1 className="project-title-with-types">{project.name}<TypeIcons available={project.template_available_types || []} active={project.output_types || []} /></h1>
          <div className="sub project-meta-row">
            <span>Status: {project.status} · wersja {project.current_version}</span>
            {(project.template_available_types || []).length > 1 && (
              <label className="project-type-select">Typ składu
                <select value={project.output_types?.[0] || ''} disabled={typeBusy || project.access_role === 'viewer'}
                  onChange={e => changeType(e.target.value)}>
                  {TEMPLATE_TYPES.filter(t => project.template_available_types.includes(t.key))
                    .map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" disabled={dupBusy} onClick={duplicate}>{dupBusy ? 'Duplikowanie…' : 'Duplikuj'}</button>
          <button className="btn btn-danger" disabled={delBusy} onClick={remove}>{delBusy ? 'Usuwanie…' : 'Usuń projekt'}</button>
        </div>
      </div>
      <div className="content">
        <Alert kind="err">{err}</Alert>
        <div className="tabs">
          {TABS.map(t => {
            // Zakładka „Treść” jest ZAWSZE dostępna (nigdy nie blokujemy użytkownika całkowicie) —
            // to tam widoczny jest JobProgress/błąd aktywnego zadania, więc de facto pełni też rolę
            // jedynego dostępnego „anuluj/przerwij” UI dopóki backend nie ma endpointu do anulowania
            // jobów (nie istnieje dziś — poza zakresem tej fazy, patrz raport).
            const disabled = t.key !== 'editor' && (!hasContent || activeJobBusy)
            const title = disabled
              ? (!hasContent ? 'Najpierw zaimportuj treść w Edytorze.' : 'Poczekaj na zakończenie bieżącego zadania (import/kompozycja) — postęp jest widoczny w Edytorze.')
              : undefined
            return (
              <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''} ${disabled ? 'tab-disabled' : ''}`}
                title={title} onClick={() => { if (disabled) return; setTab(t.key) }}>{t.label}</div>
            )
          })}
        </div>
        {tab === 'editor' && (
          <><ContentTab projectId={id} units={units} reloadUnits={() => Promise.all([reloadUnits(), reloadPagesCount()])}
            importJobId={importJobId} setImportJobId={setImportJobId} setComposeJobId={setComposeJobId} />
          {units.length > 0 && <PageViewerEditor projectId={id} units={units} reloadAll={() => { reloadUnits(); reloadPagesCount() }}
            composeJobId={composeJobId} setComposeJobId={setComposeJobId} />}</>
        )}
        {tab === 'export' && <ExportTab projectId={id} hasPages={pages.length > 0} />}
        {tab === 'versions' && <VersionsTab projectId={id} />}
        {tab === 'share' && <ShareTab projectId={id} />}
      </div>
    </>
  )
}
